import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterX, Layers, Plus } from "lucide-react";
import {
  AppButton, AppDataTable, Callout, EmptyState, FilterToolbar, ProgressBar, SegmentedControl, StatusBadge,
  applyColumnFilters, isColumnFilterActive, sortRows,
} from "../../ui";
import { DASH } from "../../../utils/format";
import { batchDates, batchSchedule } from "../../Students/StudentProfile/studentRecord";
import { batchInstructors, batchPhase, orderBatches, phaseCounts, plural, seatUsage } from "./courseDetails";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const Seats = ({ batch, active }) => {
  if (active === null) return <span className="tw-text-content-subtle">{DASH}</span>;
  const seats = seatUsage(batch, { activeStudentCount: active });
  const full = seats.cap !== null && seats.used >= seats.cap;
  return (
    <span className="tw-flex tw-min-w-[5.5rem] tw-flex-col tw-gap-1">
      <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-content">
        {seats.used}
        {seats.cap !== null && <span className="tw-text-content-subtle"> / {seats.cap}</span>}
        {full && <span className="tw-sr-only"> — full</span>}
      </span>
      {seats.cap !== null && (
        <ProgressBar value={seats.percent} color={full ? "warning" : "primary"} size="sm" />
      )}
    </span>
  );
};

/**
 * CourseBatchesTab — every batch of the course: which are running, which
 * start next, who teaches them and how full they are; and adding another.
 *
 * Client-side: the page already holds every batch (they also feed the
 * vitals), so the phase view, the name filter, sorting and paging all happen
 * here. With no sort chosen the list reads running → upcoming → completed.
 *
 * Seats are the course headcount's per-batch figure — active enrolments and
 * legacy rows, the rule the batch's own page counts — and fall back to the
 * list's own count only if the headcount could not be read.
 *
 * Props:
 *   batches   — the batch-list resource
 *   headcount — the `/courses/:id/headcount` resource
 *   onAdd     — opens the batch form for this course; absent for someone who may not
 */
const CourseBatchesTab = ({ batches, headcount, onAdd }) => {
  const navigate = useNavigate();
  const [view, setView] = useState("all");
  const [columnFilters, setColumnFilters] = useState({});
  const [sort, setSort] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const list = useMemo(() => batches.data || [], [batches.data]);
  const counts = phaseCounts(list);

  const activeIn = (b) => {
    const byBatch = headcount.data?.byBatch;
    if (byBatch) return byBatch[String(b._id)] ?? 0;
    if (headcount.loading) return null;
    return b.studentCount ?? null;
  };

  const columns = [
    {
      key: "name",
      label: "Batch",
      sortable: true,
      filter: { type: "text", placeholder: "Name contains…" },
      render: (b) => {
        const instructors = batchInstructors(b).map((i) => i.name).filter(Boolean);
        const names = instructors.length ? instructors : b.instructorNames || (b.instructor ? [b.instructor] : []);
        return (
          <span className="tw-flex tw-min-w-0 tw-flex-col">
            <span className="tw-flex tw-items-center tw-gap-2">
              <span className="tw-truncate tw-font-medium tw-text-content">{b.name || "Unnamed batch"}</span>
              {b.isLive && <StatusBadge status="live" domain="batch">Live now</StatusBadge>}
            </span>
            <span className="tw-truncate tw-text-xs tw-text-content-subtle">
              {names.length ? names.join(", ") : "No instructor yet"}
            </span>
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (b) => <StatusBadge status={String(b.status || "upcoming").toLowerCase()} domain="batch" />,
    },
    {
      key: "dates",
      label: "Dates",
      sortable: true,
      sortValue: (b) => (b.startDate ? new Date(b.startDate).getTime() : null),
      render: (b) => <span className="tw-whitespace-nowrap tw-text-content-muted">{batchDates(b) || DASH}</span>,
    },
    {
      key: "schedule",
      label: "Schedule",
      render: (b) => <span className="tw-text-content-muted">{batchSchedule(b) || DASH}</span>,
    },
    {
      key: "students",
      label: "Students",
      sortable: true,
      sortValue: (b) => activeIn(b),
      render: (b) => <Seats batch={b} active={activeIn(b)} />,
    },
    {
      key: "mode",
      label: "Mode",
      render: (b) => <span className="tw-text-content-muted">{b.mode || DASH}</span>,
    },
  ];

  const inView = view === "all" ? list : list.filter((b) => batchPhase(b) === view);
  const filtered = applyColumnFilters(inView, columns, columnFilters);
  const ordered = sort ? sortRows(filtered, columns, sort) : orderBatches(filtered);
  const totalPages = Math.max(1, Math.ceil(ordered.length / pageSize));
  const current = Math.min(page, totalPages);
  const rows = ordered.slice((current - 1) * pageSize, current * pageSize);

  const filtersOn = Object.values(columnFilters).some(isColumnFilterActive);
  const clearFilters = () => { setColumnFilters({}); setPage(1); };
  const changeView = (next) => { setView(next); setPage(1); };

  const views = [
    { value: "all", label: "All", count: counts.all },
    { value: "running", label: "Running", count: counts.running },
    { value: "upcoming", label: "Upcoming", count: counts.upcoming },
    { value: "completed", label: "Completed", count: counts.completed },
  ];

  const toolbar = (
    <FilterToolbar
      flush
      actions={onAdd && (
        <AppButton size="sm" onClick={onAdd}>
          <Plus size={16} aria-hidden="true" /> Add batch
        </AppButton>
      )}
    >
      <SegmentedControl label="Show batches" value={view} onChange={changeView} options={views} />
      <span className="tw-text-[0.8125rem] tw-text-content-subtle" aria-live="polite">
        {batches.loading && !batches.data ? "Loading…" : plural(filtered.length, "batch", "batches")}
      </span>
      {filtersOn && (
        <AppButton size="sm" variant="outline" onClick={clearFilters}>
          <FilterX size={16} aria-hidden="true" /> Clear filters
        </AppButton>
      )}
    </FilterToolbar>
  );

  let emptyState;
  if (filtersOn) {
    emptyState = (
      <EmptyState
        icon={Layers}
        title="No batches match that name"
        action={<AppButton size="sm" variant="outline" onClick={clearFilters}>Clear filters</AppButton>}
      />
    );
  } else if (counts.all === 0) {
    emptyState = (
      <EmptyState
        icon={Layers}
        title="No batches yet"
        description="A batch is one run of this course — its dates, class times, instructor and students."
        action={onAdd && (
          <AppButton onClick={onAdd}>
            <Plus size={16} aria-hidden="true" /> Add the first batch
          </AppButton>
        )}
      />
    );
  } else {
    emptyState = <EmptyState icon={Layers} title={`No ${view} batches`} />;
  }

  return (
    <>
      {batches.error && (
        <Callout
          tone="danger"
          role="alert"
          className="tw-mb-3"
          action={<AppButton size="xs" variant="outline" onClick={batches.reload}>Try again</AppButton>}
        >
          {batches.error?.response?.data?.message || "Could not load this course's batches."}
        </Callout>
      )}

      <AppDataTable
        fill={false}
        toolbar={toolbar}
        columns={columns}
        rows={rows}
        rowKey={(b) => String(b._id)}
        onRowClick={(b) => navigate(`/batches/${b._id}`)}
        sort={sort}
        onSortChange={(key, direction) => setSort({ key, direction })}
        columnFilters={columnFilters}
        onColumnFilterChange={(key, value) => {
          setColumnFilters((prev) => ({ ...prev, [key]: value }));
          setPage(1);
        }}
        loading={batches.loading && !batches.data}
        emptyState={emptyState}
        pagination={ordered.length > PAGE_SIZE_OPTIONS[0] ? {
          page: current,
          totalPages,
          total: ordered.length,
          pageSize,
          onChange: setPage,
          infoLabel: "batches",
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: (size) => { setPageSize(size); setPage(1); },
        } : undefined}
      />
    </>
  );
};

export default CourseBatchesTab;
